import { Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { tituloClinico } from "../../../utils/textoClinico";
import { origenProcedimiento, montoLegible } from "../../../utils/facturaCrear";
import "../../../styles/FacturaCrear.css";

/* ============================================================================
   CREAR FACTURA · el selector de procedimientos pendientes

   Sólo la PRESENTACIÓN del modal. La carga, el filtro, la selección, el alta y
   el borrado siguen viviendo en `ProcedimientosPendienteFactura` y se reciben
   por props: aquí no hay ni un `useState` ni una llamada a servicio.

   Qué cambia respecto al modal actual:
   · La columna "Paciente" desaparece -- el título ya dice de quién son -- y
     ese ancho pasa a Origen, que es lo que distingue duplicados.
   · El botón "Agregar" deja de estar junto al buscador, donde parecía que
     agregaba lo escrito. El buscador SÓLO filtra; agregar se decide en el pie.
   · Selección múltiple con casilla de cabecera para todo lo filtrado.
   · Las etiquetas de estado van bajo el nombre, no en columnas propias.
   · Sin scroll horizontal: el nombre es la única columna elástica.

   EL ICONO DE ELIMINAR SE CONSERVA por decisión explícita: hoy no existe otro
   sitio desde donde borrar una orden pendiente, así que quitarlo de aquí
   retiraría una capacidad real.
   ========================================================================== */

export default function ListaPendientes({
    lista,
    total,
    loading,
    searchTerm,
    onSearch,
    seleccionadas,
    onAlternarTodos,
    onAlternarUno,
    onEliminar,
}) {
    const marcadasVisibles = lista.filter((p) => seleccionadas.includes(p.key));
    const todasMarcadas = lista.length > 0 && marcadasVisibles.length === lista.length;

    return (
        <>
            <div className="fpend__filtro">
                <Input
                    placeholder="Filtrar por código o descripción"
                    value={searchTerm}
                    onChange={(e) => onSearch(e.target.value)}
                    allowClear
                />
                {total > 0 && (
                    <span className="fpend__conteo">{lista.length} de {total}</span>
                )}
            </div>

            {loading && <p className="fpend__estado">Cargando pendientes…</p>}

            {!loading && total === 0 && (
                <p className="fpend__estado">Este paciente no tiene procedimientos pendientes.</p>
            )}

            {!loading && total > 0 && lista.length === 0 && (
                <p className="fpend__estado">Ninguno coincide con «{searchTerm}».</p>
            )}

            {!loading && lista.length > 0 && (
                <div className="fpend__tabla" role="table">
                    <div className="fpend__tg fpend__cab" role="row">
                        <input
                            type="checkbox"
                            checked={todasMarcadas}
                            onChange={onAlternarTodos}
                            aria-label="Seleccionar todo lo filtrado"
                        />
                        <span className="fpend__th">Procedimiento</span>
                        <span className="fpend__th">Origen</span>
                        <span className="fpend__th fpend__th--der">Precio</span>
                        <span className="fpend__th" />
                    </div>

                    {lista.map((fila) => {
                        const origen = origenProcedimiento(fila);
                        const marcada = seleccionadas.includes(fila.key);
                        const nombre = tituloClinico(fila?.ExamenTipo?.descripcion ?? "");
                        const precio = montoLegible(fila?.precio);

                        return (
                            <label
                                className={`fpend__tg fpend__fila${marcada ? " fpend__fila--marcada" : ""}`}
                                key={fila.key}
                            >
                                <input
                                    type="checkbox"
                                    checked={marcada}
                                    onChange={() => onAlternarUno(fila)}
                                    aria-label={`Seleccionar ${nombre}`}
                                />

                                <span className="fpend__proc">
                                    <span className="fpend__nombre" title={nombre}>{nombre}</span>
                                    <span className="fcrear__proc-meta">
                                        {fila?.ExamenTipo?.codigo && (
                                            <span className="fcrear__codigo">{fila.ExamenTipo.codigo}</span>
                                        )}
                                        {/* Mismo criterio que ya existía: "Previsto" NUNCA
                                            se rotula como deuda, y sale de `grupo`, no del
                                            string crudo de `estado`. */}
                                        {fila.grupo === "PREVISTO" ? (
                                            <span className="fcrear__tag fcrear__tag--info">
                                                Previsto (aún no es deuda)
                                            </span>
                                        ) : (
                                            <span className="fcrear__tag fcrear__tag--aviso">{fila.estado}</span>
                                        )}
                                        {fila?.ExamenTipo?.contifico_id && (
                                            <span className="fcrear__tag fcrear__tag--ok">En Contífico</span>
                                        )}
                                        {fila.factura_promocion === 1 && (
                                            <span className="fcrear__tag fcrear__tag--aviso">Promoción</span>
                                        )}
                                    </span>
                                </span>

                                <span className="fcrear__origen">
                                    {origen.fecha && (
                                        <span className="fcrear__origen-fecha">{origen.fecha}</span>
                                    )}
                                    {origen.referencia && (
                                        <span className="fcrear__origen-ref">{origen.referencia}</span>
                                    )}
                                </span>

                                <span className="fpend__precio">
                                    {precio === "—" ? precio : `$${precio}`}
                                </span>

                                <button
                                    type="button"
                                    className="fpend__eliminar"
                                    title="Eliminar el procedimiento pendiente"
                                    aria-label={`Eliminar ${nombre}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onEliminar(fila);
                                    }}
                                >
                                    <DeleteOutlined />
                                </button>
                            </label>
                        );
                    })}
                </div>
            )}
        </>
    );
}
